import { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import Grid from '@mui/material/Grid'

import { RootState } from '../../../appReducer'
import {
    PracticeLocationReschedulingConfiguration,
    PracticeLocationSchedulingConfiguration,
} from '../../../models/v2/Practice'
import { fetchPracticeLocationsSchedulingDataWithAgentModule } from '../../practices/actions'
import InfoMessage from '../../shared/info-message/info-message'
import Paginator from '../../shared/Paginator'

import AppointmentsFields from './AppointmentsFields'
import CancellationsFields from './CancellationsFields'
import ReschedulingFields from './ReschedulingFields'
import {
    updatePracticeLocationReschedulingConfiguration,
    updatePracticeLocationSchedulingConfiguration,
} from './v2actions'

import './AppointmentSettingsLocationsList.sass'

export type AppointmentSettingsLocationsListProps = {
    practiceId: string
    isPending: boolean
    searchPage: number
    setSearchPage: (page: number) => void
}

const moduleName = 'appointment-settings-locations-list'

type Props = AppointmentSettingsLocationsListProps

const getEffectiveSchedulingConfiguration = (
    locationId: string,
    locationSetting: PracticeLocationSchedulingConfiguration | undefined,
): PracticeLocationSchedulingConfiguration => {
    if (locationSetting) {
        return locationSetting
    }

    return {
        practice_location_id: locationId,
        schedule_appointment: 'allow',
        new_patient_availability_months: null,
        existing_patient_availability_months: null,
    }
}

const getEffectiveConfiguration = (
    locationId: string,
    locationSetting: PracticeLocationReschedulingConfiguration | undefined,
    useGlobalSettingsChecked: boolean,
    hasGlobalSettings: boolean,
    practiceSettings: {
        cancel_appointment?: string
        reschedule_appointment?: string
        reschedule_prior?: number
        reschedule_prior_unit?: 'days' | 'hours'
        cancel_prior?: number
        cancel_prior_unit?: 'days' | 'hours'
        concurrency_num?: number
    },
): PracticeLocationReschedulingConfiguration => {
    if (useGlobalSettingsChecked && hasGlobalSettings) {
        return {
            practice_location_id: locationId,
            cancel_appointment: practiceSettings.cancel_appointment || 'allow',
            reschedule_appointment: practiceSettings.reschedule_appointment || 'allow',
            reschedule_prior: practiceSettings.reschedule_prior ?? 1,
            reschedule_prior_unit: practiceSettings.reschedule_prior_unit || 'days',
            cancel_prior: practiceSettings.cancel_prior ?? 1,
            cancel_prior_unit: practiceSettings.cancel_prior_unit || 'days',
            concurrency_num: practiceSettings.concurrency_num ?? 1,
            use_global_settings: true,
        }
    }

    if (locationSetting) {
        return locationSetting
    }

    return {
        practice_location_id: locationId,
        cancel_appointment: 'allow',
        reschedule_appointment: 'allow',
        reschedule_prior: 1,
        reschedule_prior_unit: 'days',
        cancel_prior: 1,
        cancel_prior_unit: 'days',
        concurrency_num: 1,
        use_global_settings: false,
    }
}

const AppointmentSettingsLocationsList = ({ practiceId, searchPage, setSearchPage }: Props) => {
    const loading = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.loading || false,
    )

    const list = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.list,
    )
    const pagination = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.pagination || null,
    )

    const connectedPracticeLocations = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.connectedPracticeLocations?.[practiceId] || [],
    )

    const settings = useSelector((state: RootState) => state.v2.reschedulingConfiguration.settings?.[practiceId] || [])

    const schedulingSettings = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.schedulingSettings?.[practiceId] || [],
    )

    const practiceLocationsAgentModules = useSelector(
        (state: RootState) => state.practices.practiceLocationsAgentModules[practiceId] || [],
    )

    const practiceSettingsPracticeId = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.practice_id,
    )
    const practiceSettingsCancelAppointment = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_appointment,
    )
    const practiceSettingsRescheduleAppointment = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_appointment,
    )
    const practiceSettingsConcurrencyNum = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.concurrency_num,
    )
    const practiceSettingsReschedulePrior = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_prior,
    )

    const practiceSettingsReschedulePriorUnit = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.reschedule_prior_unit,
    )

    const practiceSettingsCancelPrior = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_prior,
    )

    const practiceSettingsCancelPriorUnit = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceSettingsData?.[practiceId]?.cancel_prior_unit,
    )

    const hasGlobalSettings = Boolean(practiceSettingsPracticeId)

    const practiceSettings = {
        cancel_appointment: practiceSettingsCancelAppointment,
        reschedule_appointment: practiceSettingsRescheduleAppointment,
        reschedule_prior: practiceSettingsReschedulePrior,
        reschedule_prior_unit: practiceSettingsReschedulePriorUnit,
        cancel_prior: practiceSettingsCancelPrior,
        cancel_prior_unit: practiceSettingsCancelPriorUnit,
        concurrency_num: practiceSettingsConcurrencyNum,
    }

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchPracticeLocationsSchedulingDataWithAgentModule(practiceId))
    }, [dispatch, practiceId])

    const onUpdateSchedulingConfiguration = (
        locationSetting: PracticeLocationSchedulingConfiguration | undefined,
        locationId: string,
        configuration: PracticeLocationSchedulingConfiguration,
    ) => {
        const updatedConfiguration = {
            ...configuration,
            practice_location_id: locationId,
        }

        dispatch(
            updatePracticeLocationSchedulingConfiguration(
                practiceId,
                locationId,
                updatedConfiguration,
                !locationSetting?.practice_location_id,
            ),
        )
    }

    const onUpdateConfiguration = (
        locationSetting: PracticeLocationReschedulingConfiguration | undefined,
        locationId: string,
        configuration: PracticeLocationReschedulingConfiguration,
    ) => {
        const updatedConfiguration = {
            ...configuration,
            practice_location_id: locationId,
        }

        dispatch(
            updatePracticeLocationReschedulingConfiguration(
                practiceId,
                locationId,
                updatedConfiguration,
                !locationSetting?.practice_location_id,
            ),
        )
    }

    const handlePageChange = (nextPage: number) => {
        setSearchPage(nextPage)
    }

    const renderRows = () => {
        return (
            <>
                {Boolean(list && list.length > 0) ? (
                    list?.map(location => {
                        const locationSetting = settings.find(s => s.practice_location_id === location.id)
                        const schedulingLocationSetting = schedulingSettings.find(
                            s => s.practice_location_id === location.id,
                        )
                        const connectedLocation = connectedPracticeLocations?.find(l => l.id === location?.id)
                        const useGlobalSettingsChecked =
                            locationSetting?.use_global_settings === undefined
                                ? hasGlobalSettings
                                : locationSetting?.use_global_settings
                        const agentModuleTypeId =
                            practiceLocationsAgentModules.find(p => location.id === p.practice_location_id)
                                ?.heartbeat_agent_module?.type_id || 0
                        const isDentrixAgent = [1, 5].includes(agentModuleTypeId)
                        const effectiveConfiguration = getEffectiveConfiguration(
                            location.id,
                            locationSetting,
                            useGlobalSettingsChecked,
                            hasGlobalSettings,
                            practiceSettings,
                        )
                        const effectiveSchedulingConfiguration = getEffectiveSchedulingConfiguration(
                            location.id,
                            schedulingLocationSetting,
                        )

                        return (
                            <Fragment key={location.id}>
                                <Grid
                                    item={true}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={4}
                                    className={`${moduleName}__table-item ${moduleName}__table-item-first`}
                                    style={connectedLocation ? { borderBottom: 'none' } : undefined}
                                >
                                    {location.name}
                                </Grid>
                                {connectedLocation ? (
                                    <>
                                        <AppointmentsFields
                                            configuration={effectiveSchedulingConfiguration}
                                            onUpdate={configuration =>
                                                onUpdateSchedulingConfiguration(
                                                    schedulingLocationSetting,
                                                    location.id,
                                                    configuration,
                                                )
                                            }
                                            isOnlineSchedulingWritebackEnabled={
                                                location.is_online_scheduling_writeback_enabled !== false
                                            }
                                            practiceId={practiceId}
                                            showSidebookingPlaceholder={isDentrixAgent}
                                        />
                                        <Grid item={true} xs={12} sm={12} md={12} lg={4} />
                                        <CancellationsFields
                                            configuration={effectiveConfiguration}
                                            onUpdate={configuration =>
                                                onUpdateConfiguration(locationSetting, location.id, configuration)
                                            }
                                            disabled={useGlobalSettingsChecked}
                                            isOnlineSchedulingWritebackEnabled={
                                                location.is_online_scheduling_writeback_enabled !== false
                                            }
                                            practiceId={practiceId}
                                            showSidebookingPlaceholder={isDentrixAgent}
                                        />
                                        <Grid
                                            item={true}
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            lg={4}
                                            className={`${moduleName}__table-item-first`}
                                        />
                                        <ReschedulingFields
                                            configuration={effectiveConfiguration}
                                            onUpdate={configuration =>
                                                onUpdateConfiguration(locationSetting, location.id, configuration)
                                            }
                                            disabled={useGlobalSettingsChecked}
                                            isOnlineSchedulingWritebackEnabled={
                                                location.is_online_scheduling_writeback_enabled !== false
                                            }
                                            practiceId={practiceId}
                                            isDentrixAgent={isDentrixAgent}
                                        />
                                    </>
                                ) : (
                                    <Grid
                                        item={true}
                                        key={location.id}
                                        className={`${moduleName}__table-item ${moduleName}__info-msg`}
                                        sm={12}
                                        md={12}
                                        lg={8}
                                    >
                                        <InfoMessage isShown={true}>
                                            Practice Location not connected to an Agent.
                                        </InfoMessage>
                                    </Grid>
                                )}
                            </Fragment>
                        )
                    })
                ) : (
                    <div />
                )}
            </>
        )
    }

    return (
        <div className={`${moduleName}`}>
            <Grid className={`${moduleName}__container`} container={true}>
                {renderRows()}
            </Grid>
            {typeof list === 'undefined' && (
                <div className={`no-records`}>
                    <InfoMessage isShown={true}>
                        Please select a location, user(s), date range and click on “GET LOGS” to view configuration
                        logs.
                    </InfoMessage>
                </div>
            )}

            {Boolean(pagination) && pagination?.allRows && pagination?.allRows > 5 ? (
                <div className={`${moduleName}__paginator-wrapper`}>
                    <Paginator currentPage={searchPage} paginationInfo={pagination} selectPage={handlePageChange} />
                </div>
            ) : null}
            {loading && (
                <div className={`${moduleName}__circular-progress-loader-body`}>
                    <CircularProgress
                        className={`${moduleName}__circular-progress-spinner`}
                        size={50}
                        color="primary"
                        variant="indeterminate"
                    />
                </div>
            )}
        </div>
    )
}

export default AppointmentSettingsLocationsList
